import React, { useContext, useMemo } from 'react'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { isTickmillCompanyDisabled } from '@/utils/isTickmillCompanyDisabled'
import { CreateReferralMaterialCategory } from 'api/ClientArea/CreateReferralMaterial'
import { ReferralMaterialsCategoriesDto } from 'api/ClientArea/ReferralMaterialsDto'
import { Language } from 'api/Clients/Types/Language'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { AuthSessionContext } from 'utils/AuthContext'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'

import { ReferralMaterialsCategoryUrlsField } from './ReferralMaterialsCategoryUrlsField'

export interface ReferralMaterialsCategoryFormValues {
  name: string
  alias: string
  tickmillCompanyId: number | undefined
  urls: { baseUrl?: number; targetUrl: string }[]
  enabled: boolean
  endDateTime: string | null
}

type ReferralMaterialsCategoryFormProps = FormikProps<ReferralMaterialsCategoryFormValues> &
  OuterProps

const ReferralMaterialsCategoryFormUI: React.FC<ReferralMaterialsCategoryFormProps> = (props) => {
  const {
    data,
    handleSubmit,
    values,
    errors,
    setFieldValue,
    isSubmitting,
    isValid,
    tickmillCompanies,
    baseUrlTypes,
  } = props
  const [auth] = useContext(AuthSessionContext)

  const dateFormatType = auth?.user?.dateFormatType?.name
  const minEndDate = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }, [])
  const companyOptions = useMemo(
    () =>
      tickmillCompanies.map((company) => ({
        id: company.id,
        name: company.name,
        disabled: isTickmillCompanyDisabled(company),
      })),
    [tickmillCompanies]
  )
  const title = data?.id ? 'Edit Referral Material Category' : 'Add Referral Material Category'

  return (
    <Form>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <FormField error={errors.name} name='name' label='Category Name' />
          <FormField error={errors.alias} name='alias' label='Alias' />
          <FormSelectField
            error={errors.tickmillCompanyId}
            name='tickmillCompanyId'
            label='Tickmill Entity'
            value={values.tickmillCompanyId}
            options={companyOptions}
            showSearch
            allowClear
            onChange={(value) =>
              setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
            }
          />
          <ReferralMaterialsCategoryUrlsField
            values={values}
            isEdit
            baseUrlTypes={baseUrlTypes}
            setFieldValue={setFieldValue}
          />
          <FormDateField
            error={errors.endDateTime}
            name='endDateTime'
            label='End Date'
            dateFormatType={dateFormatType}
            isClearable
            minDate={minEndDate}
          />
          <FormCheckboxField
            error={errors.enabled}
            name='enabled'
            label='Active'
            value={values.enabled}
          />
        </div>

        <DialogFooter showCloseButton>
          <Button type='button' onClick={() => handleSubmit()} disabled={!isValid || isSubmitting}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Form>
  )
}

const dataToSubmit = (
  values: Required<ReferralMaterialsCategoryFormValues>
): CreateReferralMaterialCategory => {
  return {
    name: values.name || '',
    endDateTime: values.endDateTime
      ? createDateAsUTC(values.endDateTime, false, true).toISOString()
      : null,
    urls: values.urls
      .filter((url) => url.baseUrl)
      .map((u) => ({
        baseUrlTypeId: u.baseUrl,
        targetUrl: u.targetUrl,
      })),
    enabled: values.enabled || false,
    tickmillCompanyId: values?.tickmillCompanyId || 0,
    alias: values.alias || '',
  }
}

interface OuterProps {
  data?: ReferralMaterialsCategoriesDto
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  baseUrlTypes: NameDto[]
  onSubmit(values: FormSubmitValues<CreateReferralMaterialCategory>): Promise<void>
}

const getDefaultCompanyId = (companies: TickmillCompanyDto[]): TickmillCompanyTypes | undefined => {
  return companies.find((x) => x.id === TickmillCompanyTypes.Tickmill_SC_Company)?.id
}

export const ReferralMaterialsCategoryForm = withFormik<
  OuterProps,
  ReferralMaterialsCategoryFormValues
>({
  mapPropsToValues: (props) => {
    const { data } = props
    const defaultCompany = getDefaultCompanyId(props.tickmillCompanies)

    return {
      name: data?.name || '',
      urls: data?.urls?.length
        ? data.urls.map((u) => ({
            baseUrl: u.baseUrlType.id,
            targetUrl: u.targetUrl,
          }))
        : [{ targetUrl: '' }],
      enabled: data?.enabled ?? false,
      endDateTime: data?.endDateTime ? convertDateToUTC(data.endDateTime).toISOString() : null,
      tickmillCompanyId: data?.tickmillCompany?.id ?? defaultCompany,
      alias: data?.alias || '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<ReferralMaterialsCategoryFormValues> = {}
    const urlsErrors: { targetUrl?: string }[] = []

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.alias) {
      errors.alias = 'Required'
    }

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }

    values.urls.forEach((u, index) => {
      const urlError: { targetUrl?: string } = {}

      if (u.baseUrl === 3 && !u.targetUrl) {
        urlError.targetUrl = 'Required'
      }

      if (Object.keys(urlError).length > 0) {
        urlsErrors[index] = urlError
      }
    })

    if (urlsErrors.some(Boolean)) {
      errors.urls = urlsErrors
    }

    if (isEmpty(values.enabled)) {
      errors.enabled = 'Required'
    }

    return errors
  },
  enableReinitialize: false,
  validateOnChange: true,
  validateOnMount: true,
})(ReferralMaterialsCategoryFormUI)
